import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CountUp } from "@/components/fina/CountUp";

const RevealWord = ({ children, progress, start, end }) => {
  const opacity = useTransform(progress, [start, end], [0.15, 1]);
  return (
    <motion.span style={{ opacity }} className="inline-block mr-3">
      {children}
    </motion.span>
  );
};

export const Heritage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const paragraph =
    "Since 2015, Fina has shaped stainless steel into the invisible backbone of British industry. From pharma suites in London to hygienic food lines in Lincolnshire and water treatment plants across the Midlands — every bead we lay is measured, traceable, and built to endure.";
  const words = paragraph.split(" ");

  return (
    <section
      ref={ref}
      data-testid="heritage-section"
      className="relative bg-[#09090b] pt-12 md:pt-16 pb-24 md:pb-32 overflow-hidden ambient-depth"
    >
      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        <p className="overline mb-8" data-testid="heritage-overline">
          — The Heritage
        </p>

        <h2
          className="font-heading font-medium text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-[-0.02em] max-w-5xl"
          data-testid="heritage-copy"
        >
          {words.map((w, i) => (
            <RevealWord
              key={i}
              progress={scrollYProgress}
              start={Math.min(0.15 + i * 0.015, 0.85)}
              end={Math.min(0.18 + i * 0.015, 0.95)}
            >
              {w}
            </RevealWord>
          ))}
        </h2>

        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 relative">
          {/* Connecting line — laser-cut style */}
          <motion.span
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="hidden md:block absolute top-12 left-0 right-0 h-[1px] origin-left bg-gradient-to-r from-transparent via-[#FF5722]/40 to-transparent"
            aria-hidden
          />
          {[
            { n: 10, suffix: "+", label: "Years in metal" },
            { n: 1200, suffix: "+", label: "Projects delivered" },
            { n: 4, suffix: "", label: "Core sectors" },
            { n: 100, suffix: "%", label: "UK-built" },
          ].map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`heritage-stat-${i}`}
              className="relative"
            >
              {/* Tick mark on the connecting line */}
              <span
                aria-hidden
                className="hidden md:block absolute -top-[6px] left-0 w-[1px] h-3 bg-[#FF5722]/60"
              />
              <CountUp
                value={s.n}
                suffix={s.suffix}
                className="font-heading text-4xl md:text-5xl text-white font-medium tracking-tight tabular-nums"
              />
              <div className="mt-2 text-white/50 text-sm font-body">
                {s.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
